'use client'

import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { selectHasActiveSmartPreferences, type JourneyLeg } from '@/src/redux/slices/aiRecommendationSlice'
import { useAppSelector } from '@/src/redux/store'
import { smartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import type { ProcessedFlightMetrics } from '@/src/utils/srp/aiRecommendation/metrics'
import { CompareIndicator } from '@/src/utils/srp/aiRecommendation/ranking/types'
import { AccessTime, SentimentSatisfiedAlt, Speed, TrendingDown, TrendingUp } from '@mui/icons-material'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { Box, Chip, Divider, Stack, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useLocale } from 'next-intl'
import { FC, useMemo } from 'react'
import { MatchScore } from './matchScore'

interface SmartMetricsBadgeProps {
    metrics: ProcessedFlightMetrics | null
}

const durationConfig: Record<CompareIndicator, {
    icon: any,
    color: string,
    label: { en: string, ar: string }
}> = {
    average: {
        icon: AccessTime,
        color: "action",
        label: { en: "Similar to average duration", ar: "مشابه لمتوسط المدة" }
    },
    positive: {
        icon: Speed,
        color: "success",
        label: { en: "min faster than average", ar: "دقيقة أسرع من المتوسط" }
    },
    negative: {
        icon: AccessTime,
        color: "error",
        label: { en: "min slower than average", ar: "دقيقة أبطأ من المتوسط" }
    }
}

const priceConfig: Record<CompareIndicator, {
    icon: any,
    color: string,
    label: { en: string, ar: string }
}> = {
    average: {
        icon: TrendingUp,
        color: "action",
        label: { en: "Similar to average price", ar: "مشابه لمتوسط السعر" }
    },
    positive: {
        icon: TrendingDown,
        color: "success",
        label: { en: "cheaper than average", ar: "أرخص من المتوسط" }
    },
    negative: {
        icon: TrendingUp,
        color: "error",
        label: { en: "higher than average", ar: "أعلى من المتوسط" }
    }
}

const DEAL_COLOR_MAP: Record<string, 'success' | 'warning' | 'error'> = {
    EXCELLENT: 'success',
    GOOD: 'success',
    FAIR: 'warning',
    EXPENSIVE: 'error',
}

export const SmartMetricsBadge: FC<SmartMetricsBadgeProps> = ({
    metrics,
}) => {
    const locale = useLocale() as 'en' | 'ar'
    const theme = useTheme()
    const messages = useMemo(() => smartSRPMessages[locale], [locale])
    const { selectedJourneyLeg } = useSearchResults()
    const leg = selectedJourneyLeg as JourneyLeg
    const hasActivePreferences = useAppSelector(state => selectHasActiveSmartPreferences(state, leg))

    if (!metrics) return null

    const { matchScorePercent, dealLabel, reasons, comparison, isParetoOptimal } = metrics
    const dealText = messages.smartMetrics.dealLabels[dealLabel as keyof typeof messages.smartMetrics.dealLabels] ?? dealLabel
    const localReasons = reasons?.[locale] ?? reasons?.en ?? []
    const firstReason = localReasons?.[0]

    const durationIC = durationConfig[comparison.durationIndicator]
    const priceIC = priceConfig[comparison.priceIndicator]

    const { formatCurrency } = useFormatCurrencyForSrp()

    const absPriceDiff = Math.round(Math.abs(comparison.priceDiffFromAvg))
    const priceLabel = `${absPriceDiff ? formatCurrency(absPriceDiff) : ''} ${priceIC.label[locale]}`.trim()

    const absDurationDiff = Math.round(Math.abs(comparison.durationDiffFromAvg))
    const durationLabel = absDurationDiff
        ? `${absDurationDiff} ${durationIC.label[locale]}`
        : durationIC.label[locale]

    const dealColorKey = DEAL_COLOR_MAP[dealLabel] ?? 'warning'
    const dealColor = theme.palette[dealColorKey].main

    return (
        <Box id="smart-metrics-badge">
            <Divider />
            <Box mt={1} display="flex" alignItems="center" gap={1} flexWrap="wrap">

                {hasActivePreferences ? (
                    <Tooltip
                        title={
                            <Box>
                                <Typography fontSize={12} fontWeight={600} mb={0.5}>
                                    {`${matchScorePercent}% ${messages.smartMetrics.preferenceMatchTooltip}`}
                                </Typography>
                                {localReasons.map((reason, i) => (
                                    <Typography key={i} fontSize={11} sx={{ opacity: 0.9 }}>
                                        • {reason}
                                    </Typography>
                                ))}
                            </Box>
                        }
                        arrow
                        placement="top"
                    >
                        <Chip
                            label={dealText}
                            size="small"
                            variant="filled"
                            deleteIcon={<InfoOutlined sx={{ fontSize: 14, color: `${dealColor} !important` }} />}
                            onDelete={() => { }}
                            sx={{
                                backgroundColor: alpha(dealColor, 0.1),
                                borderColor: alpha(dealColor, 0.3),
                                border: '1px solid',
                                color: dealColor,
                                fontSize: "12px",
                                fontWeight: 600,
                                cursor: 'pointer',
                                '& .MuiChip-deleteIcon': { ml: 0 },
                            }}
                        />
                    </Tooltip>
                ) : (
                    <MatchScore value={matchScorePercent} tooltipLabel={messages.smartMetrics.scoreTooltip} />
                )}

                <Stack direction="row" spacing={1}>
                    {isParetoOptimal && (
                        <Chip
                            icon={<SentimentSatisfiedAlt color='success' fontSize='small' />}
                            label={firstReason}
                            size='small'
                            variant='filled'
                            sx={{ background: "#F3F4F7", fontSize: "12px", color: "#000" }}
                        />
                    )}
                    <Chip
                        icon={<priceIC.icon color={priceIC.color} fontSize='small' />}
                        label={priceLabel}
                        size='small'
                        variant='filled'
                        sx={{ background: "#F3F4F7", fontSize: "12px", color: "#000" }}
                    />
                    <Chip
                        icon={<durationIC.icon color={durationIC.color} fontSize='small' />}
                        label={durationLabel}
                        size='small'
                        variant='filled'
                        sx={{ background: "#F3F4F7", fontSize: "12px", color: "#000" }}
                    />
                </Stack>
            </Box>
        </Box>
    )
}
